import React, { useMemo, useState } from 'react';
import { PencilFill, CheckLg, ChevronDown } from 'react-bootstrap-icons';
import { qualificationOptions } from '../../types/schemas/subjectsandgrades';
import {
  CardHeader,
  CardContent,
  HeaderIconButton,
  AccordionItemWrapper,
  ChevronToggle,
} from '../../styles/profile/layout.style';
import {
  InfoRow,
  InfoLabel,
  InfoValue,
  Separator,
  SubjectsGrid,
  GridHeader,
  GridSubjectLabel,
  GridTickCell,
  SubheaderContainer,
  SubheaderTitle,
  SubheaderLine,
  QualificationsGrid,
  QualificationBox,
  BoxHeader,
  SubjectGradeRow,
  SubjectName,
  SubjectGrade,
  ScoreDisplay,
  MaxScoreDisplay,
  DescriptionText,
  RecommendedBadge,
} from '../../styles/profile/form.style';
import { useUser } from '../../contextManager/user.context';
import { styled } from '../../styles/stitches.config';
import QualificationsDisplay from './QualificationDisplay';
import useAuth, { ProfilePatch } from '../../hooks/auth.service';
import { friendlyTzName } from '../../utils/timezones';
import { ProfileDescriptionForm } from './ProfileDescriptionForm';
import { CheckboxDescription } from '../../styles/login.style';


interface Props {
  isMobile?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onVerificationThreat?: (field: string, isThreatened: boolean) => void;
}

export const ProfileDescription: React.FC<Props> = ({
  isMobile = false, 
  isOpen = true, 
  onToggle,
  onVerificationThreat,
}) => {
  const { userDetails } = useUser();
  const { patchProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  const subjectsByLevel = useMemo(() => {
    if (userDetails?.expertise) {
      const expertiseMap = new Map<string, Set<string>>();
      for (const [subject, levels] of Object.entries(userDetails.expertise)) {
        expertiseMap.set(subject, new Set(levels));
      }
      return expertiseMap;
    }
    return new Map();
  }, [userDetails?.expertise]);

  const subjects = useMemo(() => Array.from(subjectsByLevel.keys()).sort(), [
    subjectsByLevel,
  ]);
  
  const sortedLevels = useMemo(() => {
    if (!userDetails?.expertise) return [];
    
    const levelCounts: { [level: string]: number } = {};

    Object.values(userDetails.expertise).forEach(levelArray => {
      levelArray.forEach(level => {
        if (!levelCounts[level]) {
          levelCounts[level] = 0;
        }
        levelCounts[level]++;
      });
    });

    return Object.keys(levelCounts)
      .map(level => ({ level, count: levelCounts[level] }))
      .sort((a, b) => b.count - a.count);
  }, [userDetails?.expertise]);

  const gridColumns = useMemo(() => {
    const subjectColumn = 'max-content';
    
    const topLevels = new Set(sortedLevels.slice(0, 2).map(item => item.level));

    const levelColumns = sortedLevels.map(({ level }) => {
      if (topLevels.has(level)) {
        return 'minmax(195px, 1.5fr)';
      }
      return 'minmax(130px, 1fr)';
    }).join(' ');
    
    return `${subjectColumn} ${levelColumns}`;
  }, [sortedLevels]);

  const handleSave = async (formData: ProfilePatch) => {
    try {
      await patchProfile(
        formData,
        { loading: 'Saving profile...', success: 'Profile saved.' },
      );
      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const renderInnerContent = () => (
    <CardContent>
      <InfoRow>
        <InfoLabel>University</InfoLabel>
        <InfoValue>
          {userDetails?.college && userDetails?.university
            ? `${userDetails.college}, ${userDetails.university}`
            : userDetails?.university || 'Not Provided'}
        </InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>Degree Course</InfoLabel>
        <InfoValue>{userDetails?.degreeCourse || 'Not Provided'}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>Graduation Year</InfoLabel>
        <InfoValue>{userDetails?.gradYear || 'Not Provided'}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>Classification</InfoLabel>
        <InfoValue>{userDetails?.classification || 'Not Provided'}</InfoValue>
      </InfoRow>

      <InfoRow>
        <InfoLabel>Timezone</InfoLabel>
        <InfoValue>{userDetails?.timezone ? friendlyTzName(userDetails.timezone) : 'Not Provided'}</InfoValue>
      </InfoRow>

      <InfoRow>
        <InfoLabel>Hourly Rate</InfoLabel>
        <InfoValue> {userDetails?.hourlyRate ? `£${userDetails?.hourlyRate}/hr` : 'Not Provided'}</InfoValue>
      </InfoRow>

      <InfoRow>
        <InfoLabel>Free first lesson offered</InfoLabel>
        <InfoValue> {userDetails?.offersFreeFirstLesson ? 'Yes' : 'No'}</InfoValue>
      </InfoRow>

      <InfoRow>
      <InfoLabel>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <span>Free 15 minute introductory call offered</span>
      <RecommendedBadge>Recommended</RecommendedBadge>
    </div>
  </InfoLabel>                
        <InfoValue> {userDetails?.offersFreeIntroCall ? 'Yes' : 'No'}</InfoValue>
      </InfoRow>

      <SubheaderContainer>
        <SubheaderLine />
        <SubheaderTitle>Description</SubheaderTitle>
        <SubheaderLine />
      </SubheaderContainer>

      <DescriptionText className={!userDetails?.description ? 'not-provided' : ''}>
        {userDetails?.description || 'No description provided yet.'}
      </DescriptionText>

      <SubheaderContainer>
        <SubheaderLine />
        <SubheaderTitle>Subjects Taught</SubheaderTitle>
        <SubheaderLine />
      </SubheaderContainer>

      {subjects.length > 0 ? (
        <SubjectsGrid css={{ gridTemplateColumns: gridColumns }}>
          <GridHeader />
          {sortedLevels.map(({ level }) => (
            <GridHeader key={level}>{level}</GridHeader>
          ))}

          {subjects.map(subject => (
            <React.Fragment key={subject}>
              <GridSubjectLabel>{subject}</GridSubjectLabel>
              {sortedLevels.map(({ level }) => {
                const hasExpertise = !!subjectsByLevel.get(subject)?.has(level);
                return (
                  <GridTickCell key={level} ticked={hasExpertise}>
                    {hasExpertise && <CheckLg size={18} />}
                  </GridTickCell>
                );
              })}
            </React.Fragment>
          ))}
        </SubjectsGrid>
      ) : (
        <DescriptionText className={ 'not-provided'}>
          No subjects provided yet.
        </DescriptionText>
      )}

      <SubheaderContainer>
        <SubheaderLine />
        <SubheaderTitle>Qualifications</SubheaderTitle>
        <SubheaderLine />
      </SubheaderContainer>

      <QualificationsDisplay groups={userDetails?.qualificationGroups ?? []} />
    </CardContent>
  );

  if (isEditing) {
    return (
      <AccordionItemWrapper isOpen={true}>
      <ProfileDescriptionForm
        onSave={handleSave}
        onCancel={handleCancel}
        onVerificationThreat={onVerificationThreat}
      />
      </AccordionItemWrapper>
    );
  }

  if (isMobile) {
    return (
      <AccordionItemWrapper isOpen={isOpen}>
        <CardHeader 
          onClick={onToggle} 
          collapsed={!isOpen} 
          css={{ cursor: 'pointer' }}
        >
           <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
               <span>About</span>
           </div>
           
           <div onClick={(e) => e.stopPropagation()}>
             {isOpen && (
                <HeaderIconButton onClick={() => setIsEditing(true)}>
                  <PencilFill size={18} />
                </HeaderIconButton>
             )}
           </div>
        </CardHeader>

        {isOpen && renderInnerContent()}
      </AccordionItemWrapper>
    );
  }

  // DESKTOP VIEW
  return (
    <AccordionItemWrapper isOpen={isOpen}>
      <CardHeader
        onClick={onToggle}
        collapsed={!isOpen}
        css={{ cursor: onToggle ? 'pointer' : 'default' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ChevronToggle collapsed={!isOpen}>
            <ChevronDown />
          </ChevronToggle>
          <span>About</span>
        </div>
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
          {isOpen && (
            <HeaderIconButton onClick={() => setIsEditing(true)}>
              <PencilFill size={18} />
            </HeaderIconButton>
          )}
        </div>
      </CardHeader>

      {isOpen && renderInnerContent()}
    </AccordionItemWrapper>
  );
};